// C:\\Users\\abdul\\Downloads\\Onebox\\onebox\\src\\components\\MeetChef.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import './MeetChef.css';
import leafImage from '../assets/images/small-leaf.png';

const MeetChef = ({ tenant = {} }) => {
  const [teamData, setTeamData] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch team data and members from Supabase
  useEffect(() => {
    if (!tenant?.id) {
      setLoading(false);
      return;
    }

    fetchTeamData();
    fetchTeamMembers();

    // Subscribe to real-time updates for team section
    const sectionChannel = supabase
      .channel('team-section-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'website_sections',
          filter: `tenant_id=eq.${tenant.id}`,
        },
        (payload) => {
          console.log('🔄 Team section updated:', payload);
          if (payload.new?.section_name === 'team') {
            setTeamData(payload.new.content);
          }
        }
      )
      .subscribe();

    // Subscribe to real-time updates for team members
    const membersChannel = supabase
      .channel('team-members-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'team_members',
          filter: `tenant_id=eq.${tenant.id}`,
        },
        (payload) => {
          console.log('🔄 Team members updated:', payload);
          fetchTeamMembers();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(sectionChannel);
      supabase.removeChannel(membersChannel);
    };
  }, [tenant?.id]);

  const fetchTeamData = async () => {
    try {
      const { data, error } = await supabase
        .from('website_sections')
        .select('content, is_visible')
        .eq('tenant_id', tenant.id)
        .eq('section_name', 'team')
        .maybeSingle();

      if (error) {
        console.error('Error fetching team data:', error);
        setTeamData(getDefaultData());
      } else if (data && data.is_visible) {
        setTeamData(data.content);
        console.log('✅ Team data loaded from database');
      } else {
        setTeamData(getDefaultData());
      }
    } catch (error) {
      console.error('Error in fetchTeamData:', error);
      setTeamData(getDefaultData());
    }
  };

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .eq('tenant_id', tenant.id)
        .eq('is_active', true)
        .eq('is_featured', true)
        .is('deleted_at', null)
        .order('display_order');

      if (error) {
        console.error('Error fetching team members:', error);
        setTeamMembers([]);
      } else {
        // Transform data to match expected format
        const transformedMembers = (data || []).map(member => ({
          id: member.id,
          name: member.name,
          position: member.position,
          photo_url: member.image_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&size=400&background=FF9F0D&color=fff`,
          bio: member.bio,
          specialization: member.specialization,
          years_experience: member.years_experience,
        }));
        setTeamMembers(transformedMembers);
        console.log('✅ Team members loaded from database:', transformedMembers.length);
      }
    } catch (error) {
      console.error('Error in fetchTeamMembers:', error);
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  };

  // Default fallback data
  const getDefaultData = () => ({
    label: 'Chefs',
    title: 'Meet Our Chef',
    description: 'Passionate professionals dedicated to culinary excellence',
    button_text: 'See More',
    button_link: '#team',
    show_button: true,
    display_style: 'grid',
    show_positions: true,
    show_stats: true,
    is_active: true,
  });

  // Get data with fallbacks
  const data = teamData || getDefaultData();
  const label = data?.label ? String(data.label) : 'Chefs';
  const title = data?.title ? String(data.title) : 'Meet Our Chef';
  const description = data?.description ? String(data.description) : '';
  const buttonText = data?.button_text ? String(data.button_text) : 'See More';
  const buttonLink = data?.button_link ? String(data.button_link) : '#team';
  const showButton = data?.show_button !== undefined ? data.show_button : true;

  // Get primary color from tenant
  const primaryColor = tenant?.primary_color || '#FF9F0D';

  // Use team members from database or show placeholder
  const displayChefs = Array.isArray(teamMembers) && teamMembers.length > 0 
    ? teamMembers.slice(0, 4) // Show max 4 chefs
    : [];

  const handleButtonClick = () => {
    if (buttonLink.startsWith('#')) {
      const element = document.querySelector(buttonLink);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.location.href = buttonLink;
    }
  };

  // Show loading state (optional - can remove if you want instant render)
  if (loading) {
    return (
      <section className="chef-section">
        <div className="chef-container">
          <div className="chef-loading">
            <div className="spinner"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="chef-section">
      {/* Right Leaf Decoration */}
      <div className="chef-leaf chef-leaf-right">
        <img src={leafImage} alt="" className="leaf-img" />
      </div>

      <div className="chef-container">
        {/* Section Header */}
        <div className="chef-header">
          <span className="chef-label" style={{ color: primaryColor }}>
            {label}
          </span>
          <h2 className="chef-title">{title}</h2>
          {description && (
            <p className="chef-description">{description}</p>
          )}
        </div>

        {/* Chefs Grid */}
        {displayChefs.length > 0 ? (
          <div className="chef-grid">
            {displayChefs.map((chef) => (
              <div key={chef.id} className="chef-card">
                <div className="chef-image">
                  <img 
                    src={chef.photo_url} 
                    alt={chef.name} 
                    className="chef-img"
                    loading="lazy"
                  />
                  {/* Premium Corner Borders */}
                  <span 
                    className="corner-border corner-top-left"
                    style={{ borderColor: primaryColor }}
                  ></span>
                  <span 
                    className="corner-border corner-bottom-right"
                    style={{ borderColor: primaryColor }}
                  ></span>
                  
                  {/* Enhanced Info Overlay */}
                  <div className="chef-info">
                    <h3 className="chef-name">{chef.name}</h3>
                    <p className="chef-role">{chef.position}</p>
                    
                    {/* Experience Badge */}
                    {chef.years_experience && (
                      <div className="chef-experience">
                        <svg 
                          className="experience-icon" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                          width="14"
                          height="14"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
                          />
                        </svg>
                        <span>{chef.years_experience}+ years</span>
                      </div>
                    )}
                    
                    {/* Specialization Tags */}
                    {chef.specialization && Array.isArray(chef.specialization) && chef.specialization.length > 0 && (
                      <div className="chef-specialization">
                        {chef.specialization.slice(0, 3).map((spec, index) => (
                          <span 
                            key={index} 
                            className="spec-tag"
                            style={{ borderColor: primaryColor, color: primaryColor }}
                          >
                            {spec}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-chefs">
            <p>No team members added yet. Add team members from the dashboard.</p>
          </div>
        )}

        {/* See More Button */}
        {showButton && buttonText && displayChefs.length > 0 && (
          <div className="chef-button-wrapper">
            <button 
              className="chef-button"
              style={{ 
                borderColor: primaryColor,
                color: primaryColor 
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = primaryColor;
                e.target.style.color = '#FFFFFF';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = primaryColor;
              }}
              onClick={handleButtonClick}
            >
              {buttonText}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default MeetChef;